// App.js — router + top-level layout. Actual rendering lives in
// components/AnimatedBackground, components/AppHeader, SidebarNavigation,
// and the individual page components, each in its own chunk (lazyPage.js;
// ROUTES below is the one list of pages).

import React, { Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  matchPath,
  useLocation,
} from "react-router-dom";
import { Box, CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Helmet } from "react-helmet-async";

import theme from "./theme";
import { trackPageView } from "./analytics";
import AnimatedBackground from "./components/AnimatedBackground";
import AppHeader from "./components/AppHeader";
import SidebarNavigation from "./SidebarNavigation";
import Footer from "./components/Footer";
import { lazyPage } from "./lazyPage";

// Every page, in match order. Each is its own chunk; index.js preloads the
// first page before rendering, and links preload theirs on hover or touch.
const NotFoundPage = lazyPage(() => import("./NotFoundPage"));
const ROUTES = [
  ["/", lazyPage(() => import("./ScoresHeatmap"))],
  ["/records", lazyPage(() => import("./RecordsPage"))],
  ["/scorelines", lazyPage(() => import("./ScorelinesPage"))],
  ["/scorelines/:score", lazyPage(() => import("./ScorelinePage"))],
  ["/clubs", lazyPage(() => import("./ClubsPage"))],
  ["/clubs/:slug", lazyPage(() => import("./ClubPage"))],
  ["/all-time-table", lazyPage(() => import("./AllTimeTablePage"))],
  ["/race", lazyPage(() => import("./RacePage"))],
  ["/watch", lazyPage(() => import("./WatchPage"))],
  ["/seasons", lazyPage(() => import("./SeasonsPage"))],
  ["/seasons/:slug", lazyPage(() => import("./SeasonPage"))],
  ["/head-to-head", lazyPage(() => import("./H2HHubPage"))],
  ["/head-to-head/:pair", lazyPage(() => import("./H2HPage"))],
  ["/articles", lazyPage(() => import("./ArticlesPage"))],
  ["/articles/:slug", lazyPage(() => import("./ArticlePage"))],
  ["/about", lazyPage(() => import("./AboutPage"))],
  ["/faq", lazyPage(() => import("./FAQPage"))],
  ["/contact", lazyPage(() => import("./ContactPage"))],
  ["/privacy", lazyPage(() => import("./PrivacyPage"))],
  ["/terms", lazyPage(() => import("./TermsPage"))],
];

// Fetch the chunk for the page at `pathname` (the 404 page if none match).
export function preloadRoute(pathname) {
  const hit = ROUTES.find(([path]) => matchPath({ path, end: true }, pathname));
  return (hit ? hit[1] : NotFoundPage).preload();
}

// Sitewide JSON-LD: tells search engines this is a WebSite with a
// consistent name and URL. Injected once at the app root.
const SITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Premier League Scorigami",
  alternateName: "PL Scorigami",
  url: "https://premierleaguescorigami.com/",
  description:
    "An interactive heatmap of every Premier League scoreline ever recorded — track rare results, first and latest occurrences, and season-by-season trends.",
  inLanguage: "en",
};

function pageKeyFor(pathname) {
  if (pathname === "/records") return "records";
  if (pathname.startsWith("/scorelines")) return "scorelines";
  if (pathname.startsWith("/clubs")) return "clubs";
  if (pathname === "/all-time-table") return "table";
  if (pathname === "/race") return "race";
  if (pathname === "/watch") return "watch";
  if (pathname.startsWith("/seasons")) return "seasons";
  if (pathname.startsWith("/head-to-head")) return "h2h";
  if (pathname.startsWith("/articles")) return "articles";
  if (pathname === "/about") return "about";
  if (pathname === "/faq") return "faq";
  if (pathname === "/contact") return "contact";
  if (pathname === "/privacy") return "privacy";
  if (pathname === "/terms") return "terms";
  return "home";
}

function AppContent() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const currentPage = pageKeyFor(location.pathname);

  // One GA page view per route, after the page has set its title.
  useEffect(() => {
    const t = setTimeout(trackPageView, 0);
    return () => clearTimeout(t);
  }, [location.pathname]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflowX: "hidden",
        background: theme.palette.background.default,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(SITE_JSONLD)}
        </script>
      </Helmet>
      <AnimatedBackground />
      <AppHeader onMenuClick={() => setDrawerOpen(true)} currentPage={currentPage} />
      <SidebarNavigation
        currentPage={currentPage}
        drawerOpen={drawerOpen}
        onDrawerToggle={setDrawerOpen}
      />

      <Box component="main" sx={{ position: "relative", zIndex: 5, flex: 1 }}>
        {/* Only seen when following a link to a page whose chunk hasn't
            loaded yet (usually a split second); keeps the footer down. */}
        <Suspense fallback={<Box sx={{ minHeight: "70vh" }} />}>
          <Routes>
            {ROUTES.map(([path, Page]) => (
              <Route key={path} path={path} element={<Page />} />
            ))}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Box>

      <Footer />
    </Box>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}
